import { useMemo } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { parseRawFormData } from '../../../dashboard/components/formUtils';
import { imcsTarFormSchema } from '../../tar-imcs/IMCSTARFormSchema';

// Human-readable label lookup for the expense line items. The schema
// stores each category as a camelCase `type` (e.g. `baggageFees`) with a
// friendly `label` ("Baggage Fees"), but the saved TAR record collapses
// the type into `description = type.toUpperCase()` ("BAGGAGEFEES") —
// which reads badly. Walk the schema once and build a UPPERCASED-key →
// friendly-label map so we can restore the labels for the summary.
const EXPENSE_LABEL_BY_UPPER = (() => {
  const map = {};
  const blocks = imcsTarFormSchema?.blocks ?? [];
  const walkFields = (fields) => {
    if (!Array.isArray(fields)) return;
    fields.forEach((f) => {
      if (f?.type === 'expenseRows' && Array.isArray(f.rows)) {
        f.rows.forEach((r) => {
          if (r?.type && r?.label) {
            map[String(r.type).toUpperCase()] = r.label;
          }
        });
      }
      if (Array.isArray(f?.fields)) walkFields(f.fields);
    });
  };
  blocks.forEach((b) => walkFields(b?.fields));
  return map;
})();

// Fallback: turn a camelCase-ish string into Title Case with spaces.
// Handles the case where a category was saved with a type the schema no
// longer defines (rare, but shouldn't render as garbled ALLCAPS).
const humanizeCategory = (v) => {
  if (v == null || v === '') return '—';
  const s = String(v);
  const upper = s.toUpperCase();
  if (EXPENSE_LABEL_BY_UPPER[upper]) return EXPENSE_LABEL_BY_UPPER[upper];
  // Insert a space at camelCase boundaries, then title-case each word.
  return s
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Condensed, read-only summary of a submitted IMCS TAR. Merges the flat
 * `tarData` columns with the `rawFormData` blob so every field an
 * approver might care about is available, then lays them out in a tight
 * label / value grid grouped by section. Intentionally lighter than the
 * full DynamicForm render — meant for a scannable review, not a
 * pixel-perfect replica of the submission form.
 */
export default function TarReadOnlyView({ tarData }) {
  const merged = useMemo(() => {
    if (!tarData) return null;
    const raw = tarData.rawFormData || tarData.RawFormData;
    const parsed = raw ? parseRawFormData(raw) : {};
    // parsed values win: the form saves final user-visible values into
    // rawFormData, while top-level columns can carry stale / partial
    // shadows (e.g. FK ids where the form used freeform text).
    return { ...tarData, ...parsed };
  }, [tarData]);

  if (!tarData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const fmtDate = (v) => (v ? dayjs(v).utc().format('DD MMM YYYY') : '-');
  const fmtMoney = (v) => {
    if (v == null || v === '') return '-';
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };
  const fmtNum = (v) => (v == null || v === '' ? '-' : String(v));
  const fmtText = (v) => (v == null || v === '' ? '-' : String(v));

  // Estimated expenses land in `estimates[0].lineItems` for saved TARs.
  // Fall back to the raw `expenses` array from parsed rawFormData when
  // the flat estimates aren't populated (e.g. drafts saved locally).
  const lineItems =
    (Array.isArray(merged.estimates) && merged.estimates[0]?.lineItems) ||
    (Array.isArray(merged.expenses) ? merged.expenses : []) ||
    [];

  return (
    // Backdrop behind the section cards. Uses `background.paper` — the
    // same MUI surface tone the AuditLog DataGrid renders against —
    // so the two sections read as a matched pair when the panel is
    // expanded. Dark mode uses its own `background.paper` value from
    // the theme (no manual override).
    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        borderRadius: 1,
        backgroundColor: 'background.paper',
      }}
    >
      <Stack spacing={2}>
      <Section title="Traveler Details">
        <Grid>
          <Field label="Traveler" value={fmtText(merged.fromContactId)} />
          <Field label="Company" value={fmtText(merged.contractorName || 'TYTO Athene')} />
          <Field label="Project" value={fmtText(merged.projectName)} />
          <Field
            label="Travel Destination"
            value={fmtText(merged.eventLocation)}
          />
          <Field label="Trip Start" value={fmtDate(merged.eventStartDate)} />
          <Field label="Trip End" value={fmtDate(merged.eventEndDate)} />
          <Field label="# of Nights" value={fmtNum(merged.numberOfNights)} />
          <Field label="# of Days" value={fmtNum(merged.numberOfTripDays)} />
        </Grid>
      </Section>

      <Section title="Contract Details">
        <Grid>
          <Field label="COR" value={fmtText(merged.corName || merged.cor)} />
          <Field label="Contract" value={fmtText(merged.tarContractCode)} />
          <Field label="Task Order #" value={fmtText(merged.taskOrderNumber)} />
          <Field label="PoP Start" value={fmtDate(merged.popStart)} />
          <Field label="PoP End" value={fmtDate(merged.popEnd)} />
          <Field label="CLIN" value={fmtText(merged.clin)} />
        </Grid>
      </Section>

      {lineItems.length > 0 && (
        <Section title="Estimated Expenses">
          <Table size="small">
            <TableHead>
              {/* Lightly shaded header row + a slightly stronger bottom
                  border under the row so the column labels are set off
                  from the first line item. */}
              <TableRow
                sx={(theme) => ({
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.05)'
                      : 'rgba(0,0,0,0.03)',
                  '& > th': {
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  },
                })}
              >
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Qty
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Unit Cost
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Total
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lineItems.map((item, idx) => {
                // For freeform "Other" rows the user's typed description
                // (item.expense) is more useful than the generic "OTHER"
                // category tag. Otherwise humanize the stored category.
                const rawKey = item.description || item.type || item.category;
                const label =
                  String(rawKey || '').toUpperCase() === 'OTHER' && item.expense
                    ? item.expense
                    : humanizeCategory(rawKey);
                const qty = item.days ?? item.quantity ?? '';
                const unit =
                  item.itemUnitCost ??
                  item.unitCost ??
                  '';
                const total =
                  item.totalEstimatedCost ??
                  item.total ??
                  (Number(qty || 0) * Number(unit || 0));
                return (
                  <TableRow
                    key={idx}
                    // Subtle row divider — mirrors the light bottom
                    // border the IMCS TAR form's ExpenseRows uses so
                    // each line item is visually associated with its
                    // cost columns across the width of the table.
                    sx={{
                      '& > td': {
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      },
                    }}
                  >
                    <TableCell>{label}</TableCell>
                    <TableCell align="right">{fmtNum(qty)}</TableCell>
                    <TableCell align="right">{fmtMoney(unit)}</TableCell>
                    <TableCell align="right">{fmtMoney(total)}</TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ '& > td': { borderBottom: 'none' } }}>
                <TableCell colSpan={3} align="right" sx={{ fontWeight: 700 }}>
                  Total Estimated Trip Cost
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {fmtMoney(merged.totalNTETripCost || merged.subtotal)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {/* Attribution line — always rendered so the approver sees
              which CLIN this trip's cost invoices against. Falls back
              to `—` when a TAR pre-dates the CLIN field (older records
              never got a value stored in rawFormData). */}
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              textAlign: 'right',
              color: 'text.secondary',
              fontStyle: 'italic',
            }}
          >
            Value invoiced against CLIN {fmtText(merged.clin)}
          </Typography>
        </Section>
      )}

      <Section title="Trip Remarks">
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {fmtText(merged.purposeJustification || merged.description)}
        </Typography>
      </Section>

      <Section title="Contact Information">
        <Grid>
          <Field label="TDY POC Name" value={fmtText(merged.tdyPocName || merged.tdyPocId)} />
          <Field label="TDY POC Phone" value={fmtText(merged.tdyPocPhone)} />
          <Field label="TDY POC Email" value={fmtText(merged.tdyPocEmail)} />
          <Field
            label="Emergency Contact"
            value={fmtText(merged.emergencyContactName || merged.emergencyPocId)}
          />
          <Field
            label="Emergency Phone"
            value={fmtText(merged.emergencyContactPhone)}
          />
          <Field
            label="Emergency Email"
            value={fmtText(merged.emergencyContactEmail)}
          />
        </Grid>
      </Section>

      <Section title="Per Diem & Rates">
        <Grid>
          <Field label="Destination Postal Code" value={fmtText(merged.lodgingZipCode)} />
          <Field
            label="Daily Lodging Rate"
            value={fmtMoney(merged.dailyLodgingRate)}
          />
          <Field label="Daily M&IE Rate" value={fmtMoney(merged.dailyMieRate)} />
          <Field
            label="POV Mileage Rate"
            value={merged.mileageRate ? `$${merged.mileageRate}/mi` : '-'}
          />
        </Grid>
      </Section>
      </Stack>
    </Box>
  );
}

// Section: outlined block with a bold heading; keeps groups visually
// distinct without heavy borders. `disableGutters`-style padding so
// dense info doesn't waste vertical space.
function Section({ title, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 1.5 }} />
      {children}
    </Paper>
  );
}

// Grid: responsive 3-up label/value layout at wider widths, collapsing
// to 2 then 1 on narrower panels.
function Grid({ children }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: '1fr 1fr',
          md: '1fr 1fr 1fr',
        },
        rowGap: 1.25,
        columnGap: 3,
      }}
    >
      {children}
    </Box>
  );
}

// Field: label above value, bolded value. Matches the ViewPanel's
// compact-mode row style so the reader's eye can move between the two
// layouts without recalibrating.
function Field({ label, value }) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
        {value}
      </Typography>
    </Box>
  );
}
